import * as React from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import { Headline, Text } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import type { HeadlineVariant } from '@oneaudi/unified-web-components/dist/cjs/components/headline';
import { messages } from '../../../i18n/messages';
import { isTranslationLoaded } from '../../../lib/isTranslationLoaded';
import { ImprintModal } from '../ImprintModal';
import { ButtonAlignedLeft } from '../PartnerBusinessCardAddress';
import { useTrackingManager } from '../../useTrackingManager';
import type { HeadlineOrder, HeadlineType, VtpDealer } from '../PartnerBusinessCard';

interface Props {
  vtpData: {
    imprint?: string;
    vtpDealer: VtpDealer;
  };
  pbcRef: React.RefObject<HTMLDialogElement>;
  headline: {
    headlineTag: HeadlineType;
    headlineOrder: HeadlineOrder;
  };
}

const CustomButton = styled(ButtonAlignedLeft)`
  margin: 0 0 16px 0;
`;

const NationwideSellingContainer = styled.div`
  display: grid;
  .nationwide-selling-label,
  .nationwide-selling-new-car-info-text {
    margin: 0 0 16px 0;
  }
`;

export const NationwideSellingVehicle: React.FC<Props> = ({
  vtpData,
  pbcRef,
  headline,
}): React.ReactElement => {
  const [openDialog, setIsOpen] = React.useState(false);

  const trackingManager = useTrackingManager();
  const { headlineOrder = '3', headlineTag = 'h2' } = headline;

  const vtpTitle = useI18n(messages.vtpHeadline);
  const showTitle = isTranslationLoaded(messages.vtpHeadline.id, vtpTitle);
  const nameVariant = (() => {
    if (showTitle) return { type: 'headline', order: '4', weight: 'normal' };
    return { type: 'headline', order: headlineOrder, weight: 'normal' };
  })() as HeadlineVariant;

  const nameAs = (() => {
    if (showTitle) return 'div';
    return headlineTag;
  })();

  const phoneLabel1 = useI18n(messages.vtpNationwideSellingPhoneLabel1);

  const hasPhone = isTranslationLoaded(messages.vtpNationwideSellingPhoneLabel1.id, phoneLabel1);

  return (
    <NationwideSellingContainer>
      <Headline
        tag={nameAs}
        variant={nameVariant}
        data-testid="nationwide-selling-label"
        className="nationwide-selling-label"
      >
        {useI18n(messages.vtpNationwideSellingLabel)}
      </Headline>
      <Text
        tag="p"
        variant={{ type: 'copy', order: '2', style: 'normal' }}
        data-testid="nationwide-selling-new-car-info-text"
        className="nationwide-selling-new-car-info-text"
      >
        {useI18n(messages.vtpNationwideSellingNewCarInfoText)}
      </Text>

      {hasPhone && (
        <CustomButton
          variant="standalone"
          icon="voice-call-1"
          iconPosition="leading"
          size="md"
          href={`tel:${useI18n(messages.vtpNationwideSellingPhoneNumber)}`}
          data-testid="phone"
          onClick={() => {
            trackingManager.click('call', '', 'link', 'Anrufen', '', '2', 'phone');
          }}
        >
          {`${useI18n(messages.vtpNationwideSellingPhoneLabel1)} ${useI18n(messages.vtpNationwideSellingPhoneNumber)} ${useI18n(messages.vtpNationwideSellingPhoneLabel2)}`}
        </CustomButton>
      )}

      {vtpData?.imprint && (
        <>
          <CustomButton
            variant="standalone"
            icon="address-book"
            iconPosition="leading"
            size="md"
            onClick={() => {
              setIsOpen(!openDialog);
            }}
            data-testid="imprint-cta"
          >
            {useI18n(messages.imprintMessage)}
          </CustomButton>
          <ImprintModal
            open={openDialog}
            close={() => {
              setIsOpen(!openDialog);
            }}
            imprintRef={pbcRef}
            imprintData={vtpData.imprint}
          />
        </>
      )}
    </NationwideSellingContainer>
  );
};
